import { createAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import { getDataFormInstance, postDataFormInstance } from 'components/QuestionnaireRequestAutomated/api/processor'
import { isEmpty } from 'lodash-es'

export const attachmentsSection = -1

const initialState = {
  sections: [],
  values: {},
  unsavedValues: {},
  unsavedFieldsWithNa: {},
  fieldsWithNa: {},
  allFields: {},
  activeSection: 0,
  activeNode: 0,
  activeTab: 0,
  loading: 'idle',
  expirationData: {},
  markUnchangedFields: {},
  lastUpdatedAtData: {},
  lastSaveAt: null,
  signedSignatures: {},
}

const formInstanceSlice = createSlice({
  name: 'formInstance',
  initialState,
  reducers: {
    updateUnsavedValue: (state, action) => {
      const { id, value } = action.payload
      state.unsavedValues[id] = value
      state.markUnchangedFields[id] = false
      state.unsavedFieldsWithNa[id] = { na: false }
    },
    updateUnsavedFieldWithNa: (state, action) => {
      const { id, value } = action.payload
      state.unsavedFieldsWithNa[id] = value
      state.markUnchangedFields[id] = false
      delete state.unsavedValues[id]
    },
    saveUnsavedValues: (state, action) => {
      const { unsavedValues } = action.payload
      Object.keys(unsavedValues).forEach((key) => {
        state.values[key] = unsavedValues[key]
        delete state.unsavedValues[key]
      })
    },
    saveUnsavedFieldsWithNa: (state, action) => {
      const { unsavedFieldsWithNa } = action.payload
      Object.keys(unsavedFieldsWithNa).forEach((key) => {
        state.fieldsWithNa[key] = unsavedFieldsWithNa[key]
        delete state.unsavedFieldsWithNa[key]
      })
    },
    saveFields: (state, action) => {
      const updates = action.payload || {}
      Object.assign(state.values, updates)
    },
    resetUnsavedValues: (state, action) => {
      state.unsavedValues = {}
    },
    resetUnsavedFieldsWithNa: (state, action) => {
      state.unsavedFieldsWithNa = {}
    },
    updateActiveSection: (state, action) => {
      state.activeSection = action.payload
    },
    updateMarkUnchangedFields: (state, action) => {
      state.markUnchangedFields = { ...state.markUnchangedFields, ...action.payload }
    },
    updateLastSaveAt: (state, action) => {
      state.lastSaveAt = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getFormInstance.pending, (state, action) => {
      state.loading = 'pending'
    })
    builder.addCase(getFormInstance.fulfilled, (state, action) => {
      state.id = action.payload.id
      state.sections = action.payload.html_nodes
      state.values = action.payload.values
      state.allFields = action.payload.html_flatten_form_fields
      state.lastSaveAt = action.payload.last_saved_at
      const fieldsWithNa = { ...action.payload.fields_with_na }
      Object.keys(fieldsWithNa).forEach((key) => {
        if (fieldsWithNa[key]['na'] !== undefined) fieldsWithNa[key]['na'] = JSON.parse(fieldsWithNa[key]['na'])
      })
      state.fieldsWithNa = fieldsWithNa
      state.loading = 'success'
      state.expirationData = action.payload.expiration_data
      state.lastUpdatedAtData = action.payload.last_updated_at_data
      state.signedSignatures = action.payload.signed_signatures
    })
    builder.addCase(getFormInstance.rejected, (state, action) => {
      state.loading = 'error'
    })
  },
})

export const jumpToQuestion = createAction('formInstance/jumpToQuestion')

export const selectSections = (state) => state.formInstance.sections
export const selectActiveSection = (state) => state.formInstance.activeSection
export const selectAllFields = (state) => state.formInstance.allFields
export const selectFieldsWithNa = (state) => state.formInstance.fieldsWithNa
export const selectValues = (state) => state.formInstance.values
export const selectUnsavedValues = (state) => state.formInstance.unsavedValues
export const selectUnsavedFieldsWithNa = (state) => state.formInstance.unsavedFieldsWithNa
export const selectExpirationData = (state) => state.formInstance.expirationData
export const selectLastUpdatedAtData = (state) => state.formInstance.lastUpdatedAtData
export const selectMarkUnchangedFields = (state) => state.formInstance.markUnchangedFields
export const selectAnySignedSigntures = (state) => !isEmpty(state.formInstance.signedSignatures)

export const getCompletedFields = createSelector(
  [selectAllFields, selectFieldsWithNa, selectValues, selectExpirationData],
  (fields, fieldsWithNa, values, expirationData) =>
    Object.keys(fields).reduce((result, key) => {
      const hasValue = values[key] !== undefined || fieldsWithNa[key]?.na
      const isNotExpired =
        !expirationData[key] || new Date(expirationData[key]).setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0)
      if (hasValue && isNotExpired) result[key] = fields[key]
      return result
    }, {})
)

export const selectCompletedFields = createSelector([getCompletedFields], (completedFields) =>
  Object.keys(completedFields)
)

export const selectIncompletedFields = createSelector(
  [selectAllFields, getCompletedFields],
  (fields, completedFields) => Object.keys(fields).filter((key) => completedFields[key] === undefined)
)

export const selectQuestions = createSelector(
  [selectSections, selectActiveSection, selectAllFields, getCompletedFields, (state, isAll) => isAll],
  (sections, activeSection, allFields, completedFields, isAll) => {
    // TODO: Handle nested questions
    return sections[activeSection]?.nodes?.filter((node) => {
      if (node?.type !== 'question') return false

      const key = node?.question_attributes?.field
      if (isAll) return allFields[key]
      return !completedFields[key] && allFields[key]
    })
  }
)

export const selectSectionAndQuestionByKey = createSelector(
  [selectSections, (state, field) => field],
  (sections, field) => {
    for (const [sectionIndex, section] of sections.entries() || []) {
      for (const [nodeIndex, node] of section.nodes?.entries() || []) {
        if (node?.type === 'question' && node?.question_attributes?.field === field) {
          return [sectionIndex, nodeIndex]
        }
      }
    }
    return [null, null]
  }
)

export const selectMarkedNaFields = createSelector([selectAllFields, selectFieldsWithNa], (fields, fieldsWithNa) =>
  Object.keys(fields).filter((field) => !!fieldsWithNa[field]?.na)
)

export const selectExpiredFields = createSelector([selectExpirationData], (expirationData) =>
  Object.keys(expirationData).filter(
    (key) => new Date(expirationData[key]).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)
  )
)

export const getFormInstance = createAsyncThunk('formInstance/getFormInstance', async (formId) => {
  const response = await getDataFormInstance(formId)
  return response.data.form
})

export const saveFormInstance = createAsyncThunk('formInstance/saveFormInstance', async (_, thunkAPI) => {
  try {
    const state = thunkAPI.getState()
    const id = state.global.formInstanceId
    const unsavedValues = selectUnsavedValues(state)
    const unsavedFieldsWithNa = selectUnsavedFieldsWithNa(state)
    const formData = new FormData()
    formData.append('form[updates]', JSON.stringify(unsavedValues))
    formData.append('form[fields_with_na]', JSON.stringify(unsavedFieldsWithNa))
    const res = await postDataFormInstance(id, formData)
    thunkAPI.dispatch(updateLastSaveAt(res.data?.last_saved_at))
    thunkAPI.dispatch(saveUnsavedValues({ unsavedValues }))
    thunkAPI.dispatch(saveUnsavedFieldsWithNa({ unsavedFieldsWithNa }))
    thunkAPI.dispatch(saveFields(res.data?.updates))
  } catch (error) {}
})

export const {
  updateActiveSection,
  updateUnsavedValue,
  updateUnsavedFieldWithNa,
  saveFields,
  saveUnsavedFieldsWithNa,
  saveUnsavedValues,
  resetUnsavedValues,
  resetUnsavedFieldsWithNa,
  updateMarkUnchangedFields,
  updateLastSaveAt,
} = formInstanceSlice.actions

export default formInstanceSlice.reducer
